import React from 'react';
import { Logo } from './Logo';
import { useApp } from '../context/AppContext';
import { Mail, MapPin, Phone, ShieldCheck } from 'lucide-react';

/**
 * Legal pages. These are not optional decoration — a payment gateway's website review
 * checks that a live merchant links to all four from every page, so they stay
 * whatever else the page loses.
 */
const LEGAL_LINKS = [
  { path: '/privacy-policy', label: 'Privacy policy' },
  { path: '/terms', label: 'Terms & conditions' },
  { path: '/refund-policy', label: 'Refund & cancellation' },
  { path: '/contact', label: 'Contact us' },
];

export const Footer = () => {
  const { adminSettings, navigate } = useApp();

  const go = (event, path) => {
    event.preventDefault();
    navigate(path);
  };

  const whatsappNumber = String(adminSettings.supportWhatsapp || '').replace(/\D/g, '');

  return (
    <footer className="relative overflow-hidden bg-ink-950 text-ink-300 pb-10 grain">
      <div
        className="absolute inset-x-0 top-0 h-48 pointer-events-none"
        style={{
          background:
            'radial-gradient(70% 100% at 20% 0%, rgba(211,47,47,0.14), transparent 65%),' +
            'radial-gradient(60% 100% at 95% 0%, rgba(195,154,76,0.12), transparent 65%)',
        }}
        aria-hidden="true"
      />
      <div className="absolute inset-x-0 top-0 rule-brass" aria-hidden="true" />

      <div className="relative shell pt-10">
        <Logo className="h-10" tone="light" compact />

        <span className="pill glass-dark text-ink-200 mt-4">
          <ShieldCheck className="w-3.5 h-3.5 text-pay-400" />
          Payments secured by Cashfree
        </span>

        {/* Contact */}
        <ul className="mt-7 space-y-3 text-[0.82rem]">
          <li className="flex items-start gap-2.5">
            <MapPin className="w-4 h-4 text-brand-400 shrink-0 mt-0.5" />
            <span className="text-ink-400 leading-relaxed">{adminSettings.companyAddress}</span>
          </li>
          <li className="flex items-center gap-2.5">
            <Mail className="w-4 h-4 text-azure-400 shrink-0" />
            <a
              href={`mailto:${adminSettings.supportEmail}`}
              className="hover:text-white transition-colors break-all"
            >
              {adminSettings.supportEmail}
            </a>
          </li>
          <li className="flex items-center gap-2.5">
            <Phone className="w-4 h-4 text-emerald-400 shrink-0" />
            <a
              href={whatsappNumber ? `https://wa.me/${whatsappNumber}` : '#'}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-white transition-colors"
            >
              {adminSettings.supportWhatsapp}
            </a>
          </li>
        </ul>

        {/* Legal */}
        <nav aria-label="Legal" className="mt-7">
          <h2 className="eyebrow text-gold-500 mb-3">Legal</h2>
          <ul className="grid grid-cols-2 gap-x-6 gap-y-2.5 text-[0.82rem]">
            {LEGAL_LINKS.map((link) => (
              <li key={link.path}>
                <a
                  href={link.path}
                  onClick={(event) => go(event, link.path)}
                  className="hover:text-white transition-colors"
                >
                  {link.label}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        <dl className="mt-6 space-y-1 text-[10px] text-ink-500">
          <div>
            <dt className="inline font-semibold text-ink-400">CIN: </dt>
            <dd className="inline font-mono">{adminSettings.cin}</dd>
          </div>
          <div>
            <dt className="inline font-semibold text-ink-400">GSTIN: </dt>
            <dd className="inline font-mono">{adminSettings.gstin}</dd>
          </div>
        </dl>

        <div className="mt-7 pt-5 border-t border-white/8 flex items-center justify-between gap-4 text-[10px] text-ink-500">
          <p>
            © {new Date().getFullYear()} {adminSettings.legalName}
          </p>
          <button
            onClick={() => navigate('/admin')}
            className="hover:text-ink-300 transition-colors shrink-0"
          >
            Admin console
          </button>
        </div>
      </div>
    </footer>
  );
};
